import {useCallback, useState} from 'react';
import {NavLink} from 'react-router-dom';
import {CalendarOutlined, TableOutlined, BarsOutlined} from '@ant-design/icons';
import {Badge, Button, Checkbox, DatePicker, Divider, Space, theme, Typography} from 'antd';
import {FilterDropdownProps, TablePaginationConfig} from 'antd/es/table/interface';
import dayjs, {Dayjs} from 'dayjs';
import {dateHoursFormat} from 'helpers/formats';
import {t} from 'i18next';
import moment from 'moment';
import {AppRoutePaths} from 'routing/Routes';
import {ServiceActionListDto} from '../../../../../../PRMGApiClient';

interface DateFilterDropdownProps extends FilterDropdownProps {
  dataIndex: 'startDateTime' | 'endDateTime';
}

export const DateFilterDropdown: React.FC<DateFilterDropdownProps> = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  dataIndex,
}) => {
  const [showTime, setShowTime] = useState(false);

  const onChange = useCallback(
    (value: Dayjs | null) => {
      const result = value?.toDate();
      if (!showTime) {
        if (dataIndex === 'startDateTime') result?.setHours(0, 0, 0, 0);
        if (dataIndex === 'endDateTime') result?.setHours(23, 59, 59, 999);
      }
      result && setSelectedKeys([result?.toUTCString()]);
    },
    [dataIndex, setSelectedKeys, showTime]
  );

  return (
    <div>
      <div className="p-2 flex flex-col gap-2">
        <DatePicker
          showToday
          showTime={showTime}
          value={selectedKeys[0] ? dayjs(selectedKeys[0]) : null}
          onChange={onChange}
        />
        <Checkbox checked={showTime} onChange={() => setShowTime(!showTime)}>
          {t('Show time')}
        </Checkbox>
      </div>
      <Divider plain className="m-1" />
      <Space className="p-2 flex justify-between">
        <Button type="text" size="small" onClick={clearFilters} disabled={!selectedKeys.length}>
          {t('Reset')}
        </Button>
        <Button type="primary" size="small" onClick={() => confirm()}>
          {t('OK')}
        </Button>
      </Space>
    </div>
  );
};

export const DatetimeColumn = (date?: Date): React.ReactNode => (
  <Typography.Text>{dateHoursFormat(date)}</Typography.Text>
);

export const CalendarCell: React.FC<{date: Date; items: ServiceActionListDto[]; type: 'day' | 'month'}> = ({
  date,
  items,
  type,
}) => {
  const currentDate = moment(date.toISOString());
  const listData = [];

  for (const item of items) {
    const startDate = moment(item.startDateTime?.toISOString());
    const endDate = moment(item.endDateTime?.toISOString());

    if (currentDate.isBetween(startDate, endDate, null, '[]') || currentDate.isSame(moment(item.startDateTime), type)) {
      listData.push(item);
    }
  }
  return (
    <ul>
      {listData.map((item) => (
        <li key={item.id}>
          <Badge
            text={<NavLink to={AppRoutePaths.services.url + '/' + item.id}>{item.name}</NavLink>}
            color={getStatusColor(item.status)}
          />
        </li>
      ))}
    </ul>
  );
};

export const paginationProps: TablePaginationConfig = {
  position: ['bottomRight'],
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50', '100'],
};

export enum ServiceSegment {
  Table = 'Table',
  Calendar = 'Calendar',
  Gantt = 'Gantt',
}

export const serviceSegments = [
  {value: ServiceSegment.Table, icon: <TableOutlined />},
  {value: ServiceSegment.Calendar, icon: <CalendarOutlined />},
  {value: ServiceSegment.Gantt, icon: <BarsOutlined />},
];

type StatusColor = 'blue' | 'green' | 'orange';

export const getStatusColor = (status?: 'Completed' | 'Planned' | 'InProgress'): StatusColor => {
  let color: StatusColor = 'blue';
  if (status === 'Completed') color = 'green';
  if (status === 'InProgress') color = 'orange';
  return color;
};

export const CardTitle: React.FC<{text: string}> = ({text}) => {
  const {token} = theme.useToken();
  return <Typography.Text style={{color: token.blue}}>{text}</Typography.Text>;
};

export const DescriptionLabel: React.FC<{text: string}> = ({text}) => {
  return <Typography.Text type="secondary">{text}</Typography.Text>;
};
